import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  MenuItem,
  Box,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material'
import { Download as DownloadIcon } from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import { productService } from '../../services/productService'
import { ProductStatus } from '../../types/product'
import { usePermissions } from '../../hooks/usePermissions'
import { useGetCategoriesQuery } from '../../store/services/categoryApi'

interface ProductExportDialogProps {
  open: boolean
  onClose: () => void
  initialStatus?: ProductStatus | ''
  initialCategory?: string
}

const ProductExportDialog: React.FC<ProductExportDialogProps> = ({
  open,
  onClose,
  initialStatus = '',
  initialCategory = '',
}) => {
  const { enqueueSnackbar } = useSnackbar()
  const { hasPermission } = usePermissions()
  const canExport = hasPermission('products.bulk_export')
  const [format, setFormat] = useState<'csv' | 'xlsx'>('csv')
  const [statusFilter, setStatusFilter] = useState<ProductStatus | ''>(initialStatus)
  const [categoryFilter, setCategoryFilter] = useState(initialCategory)
  const [isExporting, setIsExporting] = useState(false)

  const { data: categoriesData, isLoading: isLoadingCategories } = useGetCategoriesQuery({ per_page: 100 })

  useEffect(() => {
    if (open) {
      setStatusFilter(initialStatus)
      setCategoryFilter(initialCategory)
    }
  }, [open, initialStatus, initialCategory])

  const handleExport = async () => {
    if (!canExport) {
      enqueueSnackbar('You do not have permission to export products', { variant: 'error' })
      return
    }
    setIsExporting(true)
    try {
      const blob = await productService.bulkExport({
        status: statusFilter || undefined,
        category_id: categoryFilter || undefined,
      })

      // Create download link
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `products_export_${new Date().toISOString().split('T')[0]}.${format}`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      enqueueSnackbar('Products exported successfully', { variant: 'success' })
      onClose()
    } catch (error: any) {
      console.error('Export error:', error)
      enqueueSnackbar(
        error.response?.data?.detail || 'Failed to export products',
        { variant: 'error' }
      )
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>Export Catalog</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
          {!canExport && (
            <Alert severity="error">
              You do not have permission to export products.
            </Alert>
          )}
          <Alert severity="info">
            Exports complete product data including B2B pricing, HSN codes, category hierarchy, packaging, and specs.
          </Alert>

          <FormControl component="fieldset">
            <FormLabel component="legend" sx={{ fontWeight: 600, fontSize: '0.875rem', mb: 0.5 }}>
              Export Format
            </FormLabel>
            <RadioGroup
              row
              value={format}
              onChange={(e) => setFormat(e.target.value as 'csv' | 'xlsx')}
            >
              <FormControlLabel
                value="csv"
                control={<Radio size="small" />}
                label="CSV (Spreadsheet)"
              />
              <FormControlLabel
                value="xlsx"
                control={<Radio size="small" />}
                label="XLSX (Excel)"
                disabled
              />
            </RadioGroup>
          </FormControl>

          <TextField
            select
            fullWidth
            size="small"
            label="Filter by Status (Optional)"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as ProductStatus | '')}
            helperText="Leave empty to export all statuses"
          >
            <MenuItem value="">All Statuses</MenuItem>
            <MenuItem value="ACTIVE">Active</MenuItem>
            <MenuItem value="DRAFT">Draft</MenuItem>
            <MenuItem value="ARCHIVED">Archived</MenuItem>
            <MenuItem value="OUT_OF_STOCK">Out of Stock</MenuItem>
            <MenuItem value="DISCONTINUED">Discontinued</MenuItem>
          </TextField>

          <TextField
            select
            fullWidth
            size="small"
            label="Filter by Category (Optional)"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            disabled={isLoadingCategories}
            helperText="Select a category or leave empty to export all categories"
          >
            <MenuItem value="">All Categories</MenuItem>
            {categoriesData?.items.map((cat) => (
              <MenuItem key={cat.id} value={cat.id}>
                {cat.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={isExporting} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleExport}
          variant="contained"
          startIcon={isExporting ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
          disabled={isExporting || !canExport}
        >
          {isExporting ? 'Exporting...' : 'Download Export'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ProductExportDialog
